// Realtime voice-call session pieces shared by the backend (createSession) and
// the web + native call clients: transcription bias, pronunciation fixes,
// scripted (force_message) lines, and Focus sessions. Pure TS.
import { coachNameDirective } from './coach'

export type CallLang = 'en' | 'es' | 'fr' | 'pt' | 'ja' | 'zh'
const callLang = (language: string | undefined): CallLang =>
  (['en', 'es', 'fr', 'pt', 'ja', 'zh'] as const).find((l) => l === language) ?? 'en'

// xAI rejects bare "es"/"pt": Spanish and Portuguese need a region.
const HINTS = new Set(['en', 'zh', 'fr', 'ja', 'es-MX', 'es-ES', 'pt-BR', 'pt-PT'])
const DEFAULT_REGION: Record<string, string> = { es: 'es-MX', pt: 'pt-BR' }

export function languageHint(language: string, locale?: string): string | undefined {
  const fromLocale = locale?.replace('_', '-')
  if (fromLocale && fromLocale.split('-')[0] === language && HINTS.has(fromLocale))
    return fromLocale
  const hint = DEFAULT_REGION[language] ?? language
  return HINTS.has(hint) ? hint : undefined
}

export const MAX_KEYTERMS = 100
const MAX_KEYTERM_CHARS = 50

// Groups in priority order; the cap keeps the earliest.
export function voiceKeyterms(groups: (string | null | undefined)[][]): string[] {
  const seen = new Set<string>()
  const out: string[] = []
  for (const group of groups)
    for (const raw of group) {
      const term = (raw ?? '').replace(/\s+/g, ' ').trim()
      const key = term.toLowerCase()
      if (!term || term.length > MAX_KEYTERM_CHARS || seen.has(key)) continue
      seen.add(key)
      out.push(term)
      if (out.length >= MAX_KEYTERMS) return out
    }
  return out
}

const DOT: Record<CallLang, string> = {
  en: 'dot',
  es: 'punto',
  fr: 'point',
  pt: 'ponto',
  ja: 'ドット',
  zh: '点',
}

// Spoken substitutions (xAI `replace`): only the audio changes, captions and
// saved transcripts keep the written form.
export function pronunciation(language: string): Record<string, string> {
  const lang = callLang(language)
  return {
    Goaltree: 'Goal tree',
    'vaboo.app': `vaboo ${DOT[lang]} app`,
    ...(lang === 'en' ? { kcal: 'calories', '5K': 'five K', '10K': 'ten K' } : {}),
  }
}

// A verbatim TTS line spoken as the assistant's turn; no response.create after.
export function forceMessage(text: string, interruptible = true) {
  return {
    type: 'conversation.item.create',
    item: {
      type: 'force_message',
      role: 'assistant',
      interruptible,
      content: [{ type: 'output_text', text }],
    },
  }
}

function joinList(items: string[], lang: CallLang): string {
  if (lang === 'ja' || lang === 'zh') return items.join('、')
  if (items.length < 2) return items.join('')
  const and = { en: 'and', es: 'y', fr: 'et', pt: 'e' }[lang]
  return `${items.slice(0, -1).join(', ')} ${and} ${items[items.length - 1]}`
}

const LINES: Record<
  CallLang,
  {
    lowMinutes: string
    focusStart: (minutes: number, task: string | undefined, every: number | null) => string
    focusCapped: (minutes: number) => string
    todayEmpty: string
    today: (list: string) => string
    habits: (list: string) => string
    calledStreak: (hi: string, days: number) => string
    calledHabits: (hi: string, list: string) => string
    calledOther: (hi: string) => string
    calledCheckin: (hi: string) => string
    calledCommitment: (hi: string, commitment: string) => string
    hi: (name: string, coach: string | null) => string
  }
> = {
  en: {
    lowMinutes: "Quick heads-up: we've got about a minute left on this call.",
    focusStart: (m, task, every) =>
      `Okay, ${m} minutes${task ? ` on ${task}` : ''}. I'll stay quiet${every ? ` and check in every ${every} minutes` : ''}. Talk to me anytime. Go!`,
    focusCapped: (m) => `To fit the call time you have left, let's make it ${m} minutes.`,
    todayEmpty: 'Your Today list is empty right now.',
    today: (list) => `On your Today list: ${list}.`,
    habits: (list) => `Habits still to do: ${list}.`,
    calledStreak: (hi, d) =>
      `${hi} Your ${d}-day streak ends tonight and I didn't want it to slip. Got a minute?`,
    calledHabits: (hi, list) =>
      `${hi} I noticed ${list} slipped the last couple of days. How are things going?`,
    calledCheckin: (hi) => `${hi} Calling for our check-in, like we planned. How's your day going?`,
    calledCommitment: (hi, c) => `${hi} Calling for our check-in. You said “${c}” — how did it go?`,
    calledOther: (hi) => `${hi} Just checking in. How are things going?`,
    hi: (name, coach) => (coach ? `Hey ${name}, it's ${coach}.` : `Hey ${name}, it's your coach.`),
  },
  es: {
    lowMinutes: 'Un aviso rápido: nos queda más o menos un minuto de llamada.',
    focusStart: (m, task, every) =>
      `Vale, ${m} minutos${task ? ` con ${task}` : ''}. Me quedo en silencio${every ? ` y te pregunto cada ${every} minutos` : ''}. Háblame cuando quieras. ¡Vamos!`,
    focusCapped: (m) => `Para que te alcance el tiempo de llamada, hagámosla de ${m} minutos.`,
    todayEmpty: 'Tu lista de Hoy está vacía ahora mismo.',
    today: (list) => `En tu lista de Hoy: ${list}.`,
    habits: (list) => `Hábitos pendientes: ${list}.`,
    calledStreak: (hi, d) =>
      `${hi} Tu racha de ${d} días termina esta noche y no quería que se perdiera. ¿Tienes un minuto?`,
    calledHabits: (hi, list) =>
      `${hi} He visto que estos últimos días te ha costado con ${list}. ¿Cómo va todo?`,
    calledCheckin: (hi) => `${hi} Te llamo para nuestro repaso, como quedamos. ¿Qué tal va tu día?`,
    calledCommitment: (hi, c) =>
      `${hi} Te llamo para nuestro repaso. Dijiste «${c}», ¿cómo te fue?`,
    calledOther: (hi) => `${hi} Solo quería saber cómo estás. ¿Cómo va todo?`,
    hi: (name, coach) => (coach ? `Hola ${name}, soy ${coach}.` : `Hola ${name}, soy tu coach.`),
  },
  fr: {
    lowMinutes: 'Petit rappel : il nous reste environ une minute d’appel.',
    focusStart: (m, task, every) =>
      `D’accord, ${m} minutes${task ? ` sur ${task}` : ''}. Je ne dis plus rien${every ? ` et je fais le point toutes les ${every} minutes` : ''}. Parle-moi quand tu veux. C’est parti !`,
    focusCapped: (m) => `Pour tenir dans ton temps d’appel, faisons ${m} minutes.`,
    todayEmpty: 'Ta liste Aujourd’hui est vide pour le moment.',
    today: (list) => `Sur ta liste Aujourd’hui : ${list}.`,
    habits: (list) => `Habitudes encore à faire : ${list}.`,
    calledStreak: (hi, d) =>
      `${hi} Ta série de ${d} jours se termine ce soir et je ne voulais pas que tu la perdes. Tu as une minute ?`,
    calledHabits: (hi, list) =>
      `${hi} J’ai remarqué que tu as un peu lâché ${list} ces deux derniers jours. Comment ça va ?`,
    calledCheckin: (hi) =>
      `${hi} Je t’appelle pour notre point, comme prévu. Comment se passe ta journée ?`,
    calledCommitment: (hi, c) =>
      `${hi} Je t’appelle pour notre point. Tu avais dit « ${c} » : comment ça s’est passé ?`,
    calledOther: (hi) => `${hi} Je prends juste de tes nouvelles. Comment ça va ?`,
    hi: (name, coach) =>
      coach ? `Salut ${name}, c’est ${coach}.` : `Salut ${name}, c’est ton coach.`,
  },
  pt: {
    lowMinutes: 'Um aviso rápido: temos mais ou menos um minuto de chamada.',
    focusStart: (m, task, every) =>
      `Beleza, ${m} minutos${task ? ` em ${task}` : ''}. Vou ficar em silêncio${every ? ` e te pergunto a cada ${every} minutos` : ''}. Fale comigo quando quiser. Vai!`,
    focusCapped: (m) => `Para caber no seu tempo de chamada, vamos fazer ${m} minutos.`,
    todayEmpty: 'Sua lista de Hoje está vazia agora.',
    today: (list) => `Na sua lista de Hoje: ${list}.`,
    habits: (list) => `Hábitos ainda por fazer: ${list}.`,
    calledStreak: (hi, d) =>
      `${hi} Sua sequência de ${d} dias termina hoje à noite e eu não queria que ela escapasse. Tem um minuto?`,
    calledHabits: (hi, list) =>
      `${hi} Percebi que nos últimos dias você deixou passar ${list}. Como vão as coisas?`,
    calledCheckin: (hi) =>
      `${hi} Liguei para o nosso check-in, como combinamos. Como está o seu dia?`,
    calledCommitment: (hi, c) =>
      `${hi} Liguei para o nosso check-in. Você disse “${c}” — como foi?`,
    calledOther: (hi) => `${hi} Só passando para saber como você está. Como vão as coisas?`,
    hi: (name, coach) => (coach ? `Oi ${name}, aqui é ${coach}.` : `Oi ${name}, aqui é seu coach.`),
  },
  ja: {
    lowMinutes: 'お知らせです。通話の残り時間はあと1分ほどです。',
    focusStart: (m, task, every) =>
      `了解、${task ? `${task}に` : ''}${m}分集中しましょう。${every ? `私は静かにしていて、${every}分ごとに声をかけます` : '私は静かにしています'}。いつでも話しかけてね。スタート！`,
    focusCapped: (m) => `残りの通話時間に合わせて、${m}分にしましょう。`,
    todayEmpty: '今日のリストは今のところ空です。',
    today: (list) => `今日のリスト：${list}。`,
    habits: (list) => `まだの習慣：${list}。`,
    calledStreak: (hi, d) =>
      `${hi}${d}日の連続記録が今夜で途切れそうなので、電話しました。少し話せる？`,
    calledHabits: (hi, list) => `${hi}ここ2日ほど${list}ができていないみたい。最近どう？`,
    calledCheckin: (hi) => `${hi}約束していたチェックインの電話です。今日はどう？`,
    calledCommitment: (hi, c) =>
      `${hi}チェックインの電話です。「${c}」って言ってたけど、どうだった？`,
    calledOther: (hi) => `${hi}ちょっと様子を聞きたくて。最近どう？`,
    hi: (name, coach) =>
      coach ? `${name}さん、${coach}です。` : `${name}さん、あなたのコーチです。`,
  },
  zh: {
    lowMinutes: '提醒一下：这次通话大约还剩一分钟。',
    focusStart: (m, task, every) =>
      `好的，${task ? `专注${task}` : '专注'}${m}分钟。我会保持安静${every ? `，每${every}分钟问候你一次` : ''}。随时可以跟我说话。开始！`,
    focusCapped: (m) => `为了不超出你剩余的通话时间，我们就做${m}分钟。`,
    todayEmpty: '你的今日列表现在是空的。',
    today: (list) => `你的今日列表：${list}。`,
    habits: (list) => `还没完成的习惯：${list}。`,
    calledStreak: (hi, d) => `${hi}你${d}天的连续记录今晚就要断了，我不想让它溜走。有一分钟吗？`,
    calledHabits: (hi, list) => `${hi}我注意到这两天${list}没跟上。最近怎么样？`,
    calledCheckin: (hi) => `${hi}按约好的时间来电，做个简短回顾。今天过得怎么样？`,
    calledCommitment: (hi, c) => `${hi}按约好的时间来电。你说过“${c}”——进展如何？`,
    calledOther: (hi) => `${hi}就是想问问你。最近怎么样？`,
    hi: (name, coach) => (coach ? `嗨，${name}，我是${coach}。` : `嗨，${name}，我是你的教练。`),
  },
}

export const lowMinutesLine = (language: string) => LINES[callLang(language)].lowMinutes

export const callGreeting = (language: string, firstName: string, coachName: string | null) =>
  LINES[callLang(language)].hi(firstName, coachName)

export function todayListLine(
  language: string,
  today: { tasks: string[]; habits: string[] },
): string {
  const lang = callLang(language)
  const l = LINES[lang]
  if (!today.tasks.length && !today.habits.length) return l.todayEmpty
  return [
    today.tasks.length ? l.today(joinList(today.tasks, lang)) : '',
    today.habits.length ? l.habits(joinList(today.habits, lang)) : '',
  ]
    .filter(Boolean)
    .join(' ')
}

// The opener when the COACH placed the call (coachCalls.slipReason details).
// Opening of the live-call system prompt. coachName null = the user's own
// cloned voice with no name given, so the coach claims no name.
export function callIdentity(coachName: string | null, firstName: string): string {
  return coachName
    ? `You are ${coachName}, ${firstName}'s personal coach, in Goaltree, talking with them out loud on a live call. ${coachNameDirective(coachName)} `
    : `You are ${firstName}'s personal coach in Goaltree, talking with them out loud on a live call. `
}

export function calledOpener(
  language: string,
  called: { reason: string; detail: string },
  firstName: string,
  coachName: string | null,
): string {
  const l = LINES[callLang(language)]
  const hi = l.hi(firstName, coachName)
  const days = /^(\d+)-day streak/.exec(called.detail)
  if (called.reason === 'streak' && days) return l.calledStreak(hi, Number(days[1]))
  const habits = /^missed 2 days of: (.+)$/.exec(called.detail)
  if (called.reason === 'habits' && habits)
    return l.calledHabits(hi, joinList(habits[1].split(', '), callLang(language)))
  if (called.reason === 'checkin') {
    const due = /^due: ([^;]+)/.exec(called.detail)
    return due ? l.calledCommitment(hi, due[1]) : l.calledCheckin(hi)
  }
  return l.calledOther(hi)
}

// ── Focus sessions ("body double") ──────────────────────────────────────────

export const FOCUS_DEFAULT_MINUTES = 25
export const FOCUS_PRESETS = [15, 25, 45, 60]
export const FOCUS_MAX_MINUTES = 120
export const FOCUS_CHECKIN_EVERY = 10

export type FocusPlan = { minutes: number; task?: string; taskId?: string }

// The line is only open for the opening, each check-in and the wrap-up, so a
// session costs about a call minute for each of those.
export function focusCallMinutes(minutes: number): number {
  return focusCheckIns(minutes).length + 2
}

// Shortens the session (fewer check-ins) until its estimated cost fits the
// call minutes left.
export function capFocusMinutes(requested: number, remainingSeconds: number) {
  const wanted = Math.min(
    FOCUS_MAX_MINUTES,
    Math.max(1, Math.round(requested || FOCUS_DEFAULT_MINUTES)),
  )
  const budget = Math.floor(remainingSeconds / 60)
  let minutes = wanted
  while (minutes > 5 && focusCallMinutes(minutes) > budget) minutes--
  return { minutes, capped: minutes < wanted }
}

// While the coach is off the line, the gap between connections stays inside
// xAI's 30-minute resumption window; past this, a fresh session is opened.
export const FOCUS_RESUME_MAX_GAP_MS = 25 * 60_000

// A short "ok" / "thanks" ends a check-in exchange early.
const ACK_WORD =
  "(ok(ay)?|k|thanks?( you)?|thx|got it|cool|sure|will do|yes|yep|great|vale|gracias|s[ií]|merci|d'?accord|oui|obrigad[oa]|valeu|beleza|sim|はい|了解|ありがとう|オッケー|うん|好的?|谢谢|嗯|行)"
const ACK = new RegExp(`^${ACK_WORD}([\\s.!,。！、]+${ACK_WORD}){0,2}[\\s.!,。！、]*$`, 'i')
export const isAck = (text: string) => ACK.test(text.trim())

// Minute marks for check-ins: every N minutes, or once halfway through a short
// session, and never in the last couple of minutes before the wrap-up.
export function focusCheckIns(minutes: number, every = FOCUS_CHECKIN_EVERY): number[] {
  if (minutes < 6) return []
  if (minutes <= every + 5) return [Math.round(minutes / 2)]
  const marks: number[] = []
  for (let m = every; m <= minutes - 3; m += every) marks.push(m)
  return marks
}

export function focusCheckInEvery(minutes: number): number | null {
  const marks = focusCheckIns(minutes)
  return marks.length === 0 ? null : marks.length === 1 ? marks[0] : FOCUS_CHECKIN_EVERY
}

export function focusStartLine(language: string, plan: FocusPlan, capped: boolean): string {
  const l = LINES[callLang(language)]
  const line = l.focusStart(plan.minutes, plan.task, focusCheckInEvery(plan.minutes))
  return capped ? `${l.focusCapped(plan.minutes)} ${line}` : line
}

const taskRef = (plan: FocusPlan) =>
  plan.task ? `"${plan.task}"${plan.taskId ? ` [${plan.taskId}]` : ''}` : 'their work'

export function focusInstructions(plan: FocusPlan): string {
  return `\nFOCUS SESSION: you are body-doubling while they work on ${taskRef(plan)} for ${plan.minutes} minutes. The app keeps time and will cue your check-ins. Stay completely silent unless they talk to you; if they do, answer in one short sentence and let them get back to work. Never start conversation, ask questions or recap on your own. If they want to stop early, call end_focus.`
}

// Opening turn for a call started as a Focus session without a plan yet.
export const FOCUS_ASK_INSTRUCTIONS =
  '\nFOCUS SESSION REQUEST: they started this call to do a focus session with you. In one short line, ask what they want to work on and for how long (suggest 25 minutes if they are unsure). As soon as you know, call start_focus with task (task_id too if you know it) and minutes. No check-in, recap or small talk.'

export function focusCheckInCue(plan: FocusPlan, minutesDone: number): string {
  const left = plan.minutes - minutesDone
  return `\nFOCUS CHECK-IN: ${minutesDone} of ${plan.minutes} minutes done, ${left} left. Say ONE short, warm check-in line (like "Halfway, how's it going?"), then stay quiet so they can keep working.`
}

export function focusWrapUpCue(plan: FocusPlan, minutesDone: number): string {
  return `\nFOCUS DONE: the focus session on ${taskRef(plan)} just ended after ${minutesDone} minutes. Say time's up in a short line and ask what they got done.${plan.taskId ? ` If they say they finished it, offer to mark it done (mark_task_done with taskId ${plan.taskId}) and only do it once they agree.` : ''} Then carry on the call normally.`
}

export const START_FOCUS_TOOL = {
  type: 'function' as const,
  name: 'start_focus',
  description:
    'Start a focus session ("body double"): you stay on the line silently while they work, with short check-ins. Use when they ask for a focus session, to work alongside them, or a Pomodoro. Pass task_id when it is a task or habit you know the id of. The app speaks the confirmation itself, so say nothing extra.',
  parameters: {
    type: 'object' as const,
    properties: {
      minutes: { type: 'number', description: 'Length in minutes (default 25)' },
      task: { type: 'string', description: 'What they will work on, in their words' },
      task_id: { type: 'string', description: 'Id of the task or habit, if known' },
    },
    required: [] as string[],
  },
}

export const END_FOCUS_TOOL = {
  type: 'function' as const,
  name: 'end_focus',
  description: 'End the running focus session early, when they say they want to stop.',
  parameters: { type: 'object' as const, properties: {}, required: [] as string[] },
}

export const READ_TODAY_TOOL = {
  type: 'function' as const,
  name: 'read_today_list',
  description:
    "Read the user's Today list out loud: must-dos, other tasks on Today and habits still due. The app speaks the list verbatim, so do not repeat it; the result has ids for follow-ups.",
  parameters: { type: 'object' as const, properties: {}, required: [] as string[] },
}

// One still from the user's camera, then a yes/no before it becomes today's
// photo check-in. The call client owns the camera and the ask.
// The model has no live view. "Can you see me?" otherwise becomes a refusal
// even while My camera is showing a picture.
export const CHECKIN_SIGHT =
  "\nTheir camera is for a habit photo check-in. You do not watch it live. When they ask for a photo check-in, agree to one, or ask if you can see them, call capture_checkin_photo with that habit's id (photo check-in habits are marked in the list). Say one short line first, then call it. Do not say you cannot see them. The app takes one frame, asks them before saving it on today's dot, and checks the habit in if it was still open. The result note is the frame: talk about the habit and whether it looks done. The card may offer one or two looks, such as a filter, a mask, face paint, or a new background. The user taps one if they want it. If the result has effect, mention that look once, about the habit. If it lists effects and has no effect, they kept the plain photo, so do not push another look. If the note says the user is in the frame and the habit is not shown, say you can see them and ask them to point the camera at the habit. Never describe their face, body, weight, age, clothing, or attractiveness. If the camera was off, the app turns My camera on and you call again once they are pointing it."

export function checkinCameraLine(on: boolean) {
  return on
    ? '\nMy camera is ON right now. Call capture_checkin_photo if they want you to look. Do not say you cannot see them.'
    : '\nMy camera is off right now. A photo check-in still starts by calling capture_checkin_photo, which turns it on.'
}

export const CAPTURE_CHECKIN_TOOL = {
  type: 'function' as const,
  name: 'capture_checkin_photo',
  description:
    "Take one still for a habit photo check-in. Use when they are showing the habit (a workout, a meal, a cleared room, the finished thing), when they ask for a photo check-in, or when they ask if you can see them. Their camera may already be on. Say one short line first (like \"Hold still, I'll grab this\"), then call it once with that habit's id. Do not say you cannot see them instead of calling this. The app shows them the photo and waits for yes or no before saving. The card may offer looks such as face paint or a new background. The result's note is the frame: talk about whether the habit looks done. If the result has effect, mention that look once. If the user is in the frame and the habit is not shown, say you can see them and ask them to point the camera at the habit. Never mention their face, body, weight, age, clothing, or attractiveness. If the result says the camera just turned on, let them point it, then call this once more. If they said no, do not ask again.",
  parameters: {
    type: 'object' as const,
    properties: {
      habit_id: { type: 'string', description: 'Habit id from their habit list' },
    },
    required: ['habit_id'],
  },
}

export type CheckinCaptureResult = {
  ok: boolean
  saved?: boolean
  checkedIn?: boolean
  alreadyDone?: boolean
  habit?: string
  note?: string
  looksDone?: 'yes' | 'no' | 'unclear'
  effects?: string[]
  effect?: string
  error?: string
  reason?: string
}

export const CALL_CLIENT_TOOLS = [
  START_FOCUS_TOOL,
  END_FOCUS_TOOL,
  READ_TODAY_TOOL,
  CAPTURE_CHECKIN_TOOL,
]
